<template>
  <div class="question-container" :class="[displayMode, { 'tree-child': displayMode === 'screening-result' ? !isRoot : depth > 0 }]">
    <div class="question">
      <div v-if="!isPresentationMode || isRoot" class="label">
        <template v-if="isPresentationMode">
          <span class="question-label-text">{{ `${$t(questionLabelI18nKey)} ${questionNumber || ''}` }}<span v-if="required" class="required-mark">*</span>:</span>
        </template>
        <template v-else>{{ `${$t(questionLabelI18nKey)}: ` }}</template>
      </div>
      <div class="content">
        <span v-if="depth > 0 && !isPresentationMode" class="depth-tag">L{{ depth }}</span>
        <span>{{ questionName }}</span>
        <span v-if="isPresentationMode && !isRoot && required" class="required-mark">*</span>
      </div>
      <QuestionTypeBadge v-if="displayMode === 'screening-result'" :questionType="questionType" />
    </div>
    <!-- 视频 -->
    <template v-if="!hideAnswer && questionType === 'video'">
      <div v-if="hasVideoResourceKey" class="answer">
        <div class="label">{{ `${$t(videoLabelI18nKey)}: ` }}</div>
        <div v-loading="loading" class="content video-container" @click="handleVideoPlayIconClick">
          <img class="video-play-icon" :src="$DjangoStaticUrl('image/video/icon_video.png')"/>
        </div>
      </div>
      <div v-else class="answer no-video-answer">
        <div class="label">{{ `${$t(answerLabelI18nKey)}: ` }}</div>
        <div class="content">{{ isHr ? $t(`${i18nPrefix}.label.notUploadedByHr`) : $t(`${i18nPrefix}.label.videoNotUploaded`) }}</div>
      </div>
      <div class="remarks">
        <div class="label">{{ `${$t(noteLabelI18nKey)}: ` }}</div>
        <div class="content">{{ answer|formatValue }}</div>
      </div>
    </template>
    <!-- 附件 -->
    <template v-else-if="!hideAnswer && questionType === 'portfolio' && hasVideoResourceKey">
      <div class="answer attachment">
        <div class="label"></div>
        <div class="content">
          <el-button v-loading="loading" class="view-btn" element-loading-spinner="el-icon-loading" type="text" @click="handleViewAttachmentBtnClick">
            {{ $t('common.btn.view') }}
          </el-button>
        </div>
      </div>
      <div class="remarks">
        <div class="label">{{ `${$t(noteLabelI18nKey)}: ` }}</div>
        <div class="content">{{ answer|formatValue }}</div>
      </div>
    </template>
    <template v-else-if="!hideAnswer">
      <div class="answer text">
        <div class="label">{{ `${$t(answerLabelI18nKey)}: ` }}</div>
        <div class="content">
          <span v-if="isPresentationMode && isChoiceAnswer" class="selected-choice-icon" aria-hidden="true"><span></span></span>
          <span class="answer-value">{{ answer|formatValue }}</span>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import _ from 'lodash'
import QuestionTypeBadge from '@/components/openQuestion/questionTypeBadge.vue'

export default {
  components: {
    QuestionTypeBadge
  },
  props: {
    isHr: {
      type: Boolean,
      default: false
    },
    questionName: {
      type: String,
      required: true
    },
    questionType: {
      required: true
    },
    answer: {
      required: true
    },
    videoResourceKey: {
      required: true
    },
    hideAnswer: {
      type: Boolean,
      default: false
    },
    questionLabelI18nKey: {
      default: 'admin.application.candidate.label.questionnaireQuestion'
    },
    answerLabelI18nKey: {
      default: 'admin.application.candidate.label.answer'
    },
    videoLabelI18nKey: {
      default: 'admin.application.candidate.label.video'
    },
    noteLabelI18nKey: {
      default: 'admin.application.candidate.label.note'
    },
    depth: {
      type: Number,
      default: 0
    },
    parentQuestionId: {
      type: Number,
      default: null
    },
    displayMode: {
      type: String,
      default: ''
    },
    questionNumber: {
      type: Number,
      default: null
    },
    isRoot: {
      type: Boolean,
      default: false
    },
    required: {
      type: Boolean,
      default: false
    },
    relationIncomplete: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      i18nPrefix: 'admin.application.candidate',
      loading: false
    }
  },
  computed: {
    isPresentationMode () {
      return ['screening-result', 'hr-sidebar'].includes(this.displayMode)
    },
    hasVideoResourceKey () {
      return !_.isEmpty(this.videoResourceKey)
    },
    isChoiceAnswer () {
      return ['multiple_select', 'single_select', 'dropdown', 'checkbox'].includes(this.questionType) &&
        this.answer !== null && this.answer !== undefined && String(this.answer).trim() !== ''
    }
  },
  created () {

  },
  methods: {
    handleVideoPlayIconClick () {
      this.loading = true
      this.$emit('videoPlayIconClick', () => {
        this.loading = false
      })
    },
    handleViewAttachmentBtnClick () {
      this.loading = true
      this.$emit('viewAttachmentBtnClick', () => {
        this.loading = false
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.question-container {
  margin-top: 20px;
  padding: 25px 40px;
  border: 1px solid #E4E7ED;
  font-size: 16px;

  &.tree-child {
    background: #fafafa;
    border-left: 3px solid #0096cc;
  }

  .question,
  .answer,
  .remarks {
    display: flex;
    align-items: flex-start;
    line-height: 24px;
    white-space: break-spaces;

    .label {
      width: 84px;
      flex-shrink: 0;
      text-align: right;
    }

    .content {
      flex: 1;
      margin-left: 15px;

      .depth-tag {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        background: #0096cc;
        color: #fff;
        padding: 2px 8px;
        border-radius: 4px;
        font-size: 12px;
        font-weight: bold;
        margin-right: 8px;
      }

      &.video-container {
        flex: initial;
        position: relative;
        width: 164px;
        height: 78px;
        cursor: pointer;
        background-image: url($baseDomain + 'static/image/video/video_bg.png');
        background-size: 100% 100%;

        .video-play-icon {
          position: absolute;
          top: 50%;
          left: 50%;
          width: 40px;
          height: 40px;
          transform: translate(-50%, -50%);
        }
      }
    }
  }

  .remarks {
    margin-top: 20px;
  }

  .answer {
    margin-top: 20px;

    &.attachment {
      .content {
        .view-btn {
          position: relative;

          ::v-deep .el-loading-mask {
            background-color: rgba(251, 251, 251, .9);

            .el-loading-spinner {
              font-size: 20px;
              margin-top: -10px;
            }
          }
        }
      }
    }
  }
}

.question-container.screening-result {
  margin-top: 0;
  padding: 0;
  border: 0;
  color: #606266;

  .question,
  .answer,
  .remarks {
    padding: 16px 20px;
    line-height: 16px;

    > .content {
      padding: 0;
    }

    .label {
      width: 90px;
      text-align: left;
      font-size: 16px;
      font-weight: 400;
      white-space: nowrap;
    }

    .content {
      min-width: 0;
      margin-left: 40px;
      overflow-wrap: anywhere;
    }
  }

  .remarks {
    padding-top: 0;
  }

  .question {
    align-items: center;
    background: #fafafa;
    font-size: 16px;

    .content {
      font-weight: 500;
    }

    > :last-child {
      flex-shrink: 0;
      margin-left: auto;
    }
  }

  &.tree-child .question .content {
    margin-left: 0;
  }

  .answer,
  .remarks {
    margin-top: 0;
    background: #fff;
    font-size: 14px;
    line-height: 21px;

    .label {
      font-size: 14px;
      line-height: 21px;
    }

    .content {
      display: flex;
      gap: 8px;
    }

    .selected-choice-icon {
      margin-top: 3px;
      display: inline-flex;
      flex: 0 0 14px;
      align-items: center;
      justify-content: center;
      width: 14px;
      height: 14px;
      border-radius: 50%;
      background: #0096cc;

      > span {
        width: 4px;
        height: 4px;
        border-radius: 50%;
        background: #fff;
      }
    }

    .answer-value {
      min-width: 0;
      white-space: break-spaces;
    }
  }

  .answer.attachment .view-btn {
    padding-top: 0;
    padding-bottom: 0;
  }

  .required-mark {
    color: #f56c6c;
  }
}

.question-container.hr-sidebar {
  margin-top: 0;
  padding: 0;
  border: 0;
  color: #606266;
  font-size: 14px;

  .question,
  .answer,
  .remarks {
    line-height: 20px;

    > .content {
      padding: 0;
    }

    .label {
      width: auto;
      text-align: left;
      white-space: nowrap;
    }

    .content {
      min-width: 0;
      margin-left: 4px;
      overflow-wrap: anywhere;
    }
  }

  .question {
    display: block;
    font-weight: 400;
    white-space: normal;

    .label {
      display: inline;
      margin-right: 0;
    }

    .content {
      display: inline;
      margin-left: 0;
    }
  }

  &.tree-child {
    background: transparent;
    border-left: 0;

    .question {
      color: #676767;
      line-height: 20px;

      .content {
        margin-left: 0;
      }
    }
  }

  .answer,
  .remarks {
    margin-top: 8px;
    line-height: 14px;

    .label {
      display: none;
    }

    .content {
      display: flex;
      gap: 8px;
      margin-left: 0;
      line-height: 21px;
    }

    .selected-choice-icon {
      margin-top: 3px;
      display: inline-flex;
      flex: 0 0 14px;
      align-items: center;
      justify-content: center;
      width: 14px;
      height: 14px;
      border-radius: 50%;
      background: #0096cc;

      > span {
        width: 3.5px;
        height: 3.5px;
        border-radius: 50%;
        background: #fff;
      }
    }

  }

  &:not(.tree-child) .question + .answer {
    margin-top: 10px;
  }

  .required-mark {
    color: #f56c6c;
  }
}

// Candidate Profile has a later outer `.content` rule with equal specificity.
// Repeat the component class so these opt-in modes keep the Figma row heights.
.question-container.question-container {
  &.screening-result,
  &.hr-sidebar {
    .question,
    .answer,
    .remarks {
      > .content {
        padding: 0;
      }
    }
  }
}
</style>
